import qz from "qz-tray";
import { API } from "./api";
import { parseWeight } from "./scale";

let configured = false;
let certConfigured = false;
let connectPromise = null;
let status = { active: false, checked: false, printers: [], error: "" };
const listeners = new Set();
function emit() { for (const l of listeners) { try { l(status); } catch { /* noop */ } } }
export function onQzStatus(cb) { listeners.add(cb); cb(status); return () => listeners.delete(cb); }
export function qzStatus() { return status; }
export function qzCertConfigured() { return certConfigured; }

async function configureQz() {
  if (configured) return;
  configured = true;
  try {
    const res = await fetch(`${API}/qz/certificate`);
    const { certificate, configured } = await res.json();
    certConfigured = !!configured;
    if (certificate) {
      qz.security.setCertificatePromise((resolve) => resolve(certificate));
      qz.security.setSignatureAlgorithm("SHA512");
      qz.security.setSignaturePromise((toSign) => (resolve, reject) => {
        fetch(`${API}/qz/sign`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ toSign }),
        })
          .then((r) => (r.ok ? r.text() : Promise.reject(new Error("sign failed"))))
          .then(resolve)
          .catch(reject);
      });
    }
    // Sin certificado => modo demo (sin firmar). QZ pedirá permiso una vez.
  } catch {
    /* modo demo sin firma */
  }
}

export function qzActive() {
  return qz.websocket.isActive();
}

export async function connectQz() {
  await configureQz();
  if (qz.websocket.isActive()) return;
  if (!connectPromise) {
    connectPromise = qz.websocket.connect().finally(() => {
      connectPromise = null;
    });
  }
  return connectPromise;
}

export async function listPrinters() {
  await connectQz();
  const p = await qz.printers.find();
  const list = Array.isArray(p) ? p : [p].filter(Boolean);
  status = { ...status, active: true, checked: true, printers: list, error: "" };
  emit();
  return list;
}

// Detección automática al cargar: intenta conectar en silencio y lista impresoras.
let detecting = null;
export function detectQz() {
  if (detecting) return detecting;
  detecting = (async () => {
    try {
      await configureQz();
      if (!qz.websocket.isActive()) await qz.websocket.connect({ retries: 1, delay: 1 });
      await listPrinters();
    } catch (e) {
      status = { active: qz.websocket.isActive(), checked: true, printers: [], error: String(e?.message || e || "QZ Tray no detectado") };
      emit();
    } finally { detecting = null; }
    return status;
  })();
  return detecting;
}
try {
  qz.websocket.setClosedCallbacks(() => { status = { ...status, active: false }; emit(); });
  qz.websocket.setErrorCallbacks(() => { status = { ...status, active: qz.websocket.isActive() }; emit(); });
} catch { /* noop */ }

export async function listSerialPorts() {
  await connectQz();
  const p = await qz.serial.findPorts();
  return Array.isArray(p) ? p : [p].filter(Boolean);
}

export async function disconnectQz() {
  return qz.websocket.isActive() ? qz.websocket.disconnect() : null;
}

// Imprime un PDF (base64) por QZ Tray en la impresora de tickets (ancho en mm). Lanza si QZ no está disponible.
export async function qzPrintPdfBase64(printer, b64, widthMm = 80) {
  await connectQz();
  const config = qz.configs.create(printer || undefined, { units: "mm", size: { width: widthMm }, margins: 0, orientation: "portrait", scaleContent: true, rasterize: true, density: 203, fallbackDensity: 203 });
  return qz.print(config, [{ type: "pixel", format: "pdf", flavor: "base64", data: b64 }]);
}

export async function openDrawer(printer) {
  // Pulso de apertura del cajón (ESC/POS): ESC p 0 25 250 = 1B 70 00 19 FA
  await connectQz();
  const config = qz.configs.create(printer || undefined, {});
  const data = [{ type: "raw", format: "hex", data: "1B700019FA" }];
  return qz.print(config, data);
}

let scaleCbInstalled = false;
let currentScaleSink = null;

function ensureScaleCallback() {
  if (scaleCbInstalled) return;
  scaleCbInstalled = true;
  qz.serial.setSerialCallbacks((evt) => {
    if (!currentScaleSink) return;
    if (evt.type === "ERROR") { currentScaleSink.error(new Error(evt.exception || evt.message || "Error en el puerto serial")); return; }
    if (currentScaleSink.port && evt.portName && String(evt.portName).toUpperCase() !== String(currentScaleSink.port).toUpperCase()) return;
    currentScaleSink.data(String(evt.output ?? evt.data ?? ""));
  });
}

// Lee el peso vía QZ Tray (puerto COM). opts: { baud, unit, pollCmd, pollMs } (ver scaleOptsFor)
export async function readWeightQz(port, opts = {}, timeoutMs = 5000) {
  if (!port) throw new Error("Selecciona el puerto COM de la báscula en Configuración → Estaciones");
  if (typeof opts !== "object") opts = { baud: opts };
  await connectQz();
  ensureScaleCallback();
  const unit = opts.unit || "kg";
  const pollCmd = opts.pollCmd ? opts.pollCmd.replace(/\\r/g, "\r").replace(/\\n/g, "\n") : null;
  return new Promise((resolve, reject) => {
    let settled = false, buf = "", idle = null, poll = null;
    const hist = [];
    const finish = async (fn, arg) => {
      if (settled) return;
      settled = true; currentScaleSink = null;
      if (poll) clearInterval(poll); if (idle) clearTimeout(idle);
      try { await qz.serial.closePort(port); } catch { /* ignore */ }
      fn(arg);
    };
    const consider = (text) => {
      const w = parseWeight(text, unit);
      if (w === null || w <= 0) return;
      hist.push(w); if (hist.length > 4) hist.shift();
      const stable = hist.length >= 2 && hist.every((x) => Math.abs(x - w) < 0.003);
      if (stable || !pollCmd) finish(resolve, w);
    };
    currentScaleSink = {
      port,
      error: (e) => finish(reject, e),
      data: (chunk) => {
        buf += chunk;
        let idx;
        while ((idx = buf.search(/[\r\n\x03\x04]/)) >= 0) { const line = buf.slice(0, idx).trim(); buf = buf.slice(idx + 1); if (line) consider(line); }
        if (idle) clearTimeout(idle);
        if (buf) idle = setTimeout(() => { const t = buf.trim(); buf = ""; if (t) consider(t); }, 120);
      },
    };
    qz.serial
      .openPort(port, { baudRate: Number(opts.baud) || 9600, dataBits: 8, stopBits: 1, parity: "NONE", flowControl: "NONE", encoding: "ISO-8859-1", rx: {} })
      .then(() => {
        if (!pollCmd) return;
        const send = () => qz.serial.sendData(port, pollCmd).catch(() => {});
        send(); poll = setInterval(send, Number(opts.pollMs) || 500);
      })
      .catch((e) => finish(reject, new Error(/in use|busy|Access/i.test(String(e?.message || e)) ? `El puerto ${port} está ocupado por otro programa (software de la báscula o báscula directa). Ciérralo y reintenta.` : String(e?.message || e))));
    setTimeout(() => {
      const lastW = hist[hist.length - 1];
      lastW > 0 ? finish(resolve, lastW) : finish(reject, new Error(`Sin lectura de la báscula en ${port}. Revisa cable, baudios (${opts.baud || 9600}) y que el protocolo (${opts.pollCmd ? "comando " + JSON.stringify(opts.pollCmd) : "continuo"}) coincida con el modelo.`));
    }, timeoutMs);
  });
}
